import { Languages } from '../shared/enum/languages.enum';

/** One plate a guest can ask the food map for. Two only when the room is honestly both. */
export type SpecialtyNames = Record<Languages, string>;

const names = (
    en: string,
    de: string,
    ru: string,
    es: string,
    pl: string,
    fr: string,
    it: string,
): SpecialtyNames => ({
    [Languages.EN]: en,
    [Languages.DE]: de,
    [Languages.RU]: ru,
    [Languages.ES]: es,
    [Languages.PL]: pl,
    [Languages.FR]: fr,
    [Languages.IT]: it,
});

function specialty<const T extends string>(code: T, label: SpecialtyNames) {
    return { code, names: label };
}

export const ESTABLISHMENT_SPECIALTIES = [
    specialty('duck', names('Duck', 'Ente', 'Утка', 'Pato', 'Kaczka', 'Canard', 'Anatra')),
    specialty('sushi', names('Sushi', 'Sushi', 'Суши', 'Sushi', 'Sushi', 'Sushi', 'Sushi')),
    specialty(
        'bun-cha',
        names('Bún chả', 'Bún chả', 'Бун ча', 'Bún chả', 'Bún chả', 'Bún chả', 'Bún chả'),
    ),
    specialty('cha-ca', names('Chả cá', 'Chả cá', 'Ча ка', 'Chả cá', 'Chả cá', 'Chả cá', 'Chả cá')),
    specialty('pho', names('Phở', 'Phở', 'Фо', 'Phở', 'Phở', 'Phở', 'Phở')),
    specialty(
        'pad-thai',
        names('Pad thai', 'Pad Thai', 'Пад тай', 'Pad thai', 'Pad thai', 'Pad thaï', 'Pad thai'),
    ),
    specialty(
        'oyster-omelette',
        names(
            'Oyster omelette',
            'Austernomelett',
            'Устричный омлет',
            'Tortilla de ostras',
            'Omlet z ostryg',
            'Omelette aux huîtres',
            'Frittata di ostriche',
        ),
    ),
    specialty(
        'kuay-chap',
        names(
            'Kuay chap',
            'Kuay Chap',
            'Квай чап',
            'Kuay chap',
            'Kuay chap',
            'Kuay chap',
            'Kuay chap',
        ),
    ),
    specialty(
        'hainanese-chicken-rice',
        names(
            'Hainanese chicken rice',
            'Hainan-Hühnerreis',
            'Хайнаньский рис с курицей',
            'Arroz con pollo de Hainan',
            'Ryż z kurczakiem po hainańsku',
            'Riz au poulet de Hainan',
            'Riso al pollo di Hainan',
        ),
    ),
    specialty(
        'tom-yum-noodle',
        names(
            'Tom yum noodle',
            'Tom-Yam-Nudeln',
            'Том ям с лапшой',
            'Fideos tom yum',
            'Makaron tom yum',
            'Nouilles tom yum',
            'Noodles tom yum',
        ),
    ),
    specialty(
        'pork-noodle',
        names(
            'Pork noodle',
            'Schweine-Nudeln',
            'Свиная лапша',
            'Fideos de cerdo',
            'Makaron wieprzowy',
            'Nouilles au porc',
            'Noodles di maiale',
        ),
    ),
    specialty(
        'hainanese-beef-noodle',
        names(
            'Hainanese beef noodle',
            'Hainan-Rindernudeln',
            'Хайнаньская говяжья лапша',
            'Fideos de ternera de Hainan',
            'Makaron wołowy po hainańsku',
            'Nouilles au bœuf de Hainan',
            'Noodles di manzo di Hainan',
        ),
    ),
    specialty(
        'dry-beef-noodle',
        names(
            'Dry beef noodle',
            'Trockene Rindernudeln',
            'Сухая говяжья лапша',
            'Fideos secos de ternera',
            'Suchy makaron wołowy',
            'Nouilles sèches au bœuf',
            'Noodles secchi di manzo',
        ),
    ),
    specialty(
        'hokkien-mee',
        names(
            'Hokkien mee',
            'Hokkien Mee',
            'Хоккиен-ми',
            'Hokkien mee',
            'Hokkien mee',
            'Hokkien mee',
            'Hokkien mee',
        ),
    ),
    specialty('ramen', names('Ramen', 'Ramen', 'Рамен', 'Ramen', 'Ramen', 'Ramen', 'Ramen')),
    specialty('roti', names('Roti', 'Roti', 'Роти', 'Roti', 'Roti', 'Roti', 'Roti')),
    specialty(
        'mango-sticky-rice',
        names(
            'Mango sticky rice',
            'Mango mit Klebreis',
            'Манго со сладким рисом',
            'Mango con arroz glutinoso',
            'Mango z ryżem kleistym',
            'Mangue et riz gluant',
            'Mango e riso glutinoso',
        ),
    ),
    specialty(
        'croissant',
        names(
            'Croissant',
            'Croissant',
            'Круассан',
            'Cruasán',
            'Croissant',
            'Croissant',
            'Croissant',
        ),
    ),
    specialty('bagel', names('Bagel', 'Bagel', 'Бейгл', 'Bagel', 'Bajgiel', 'Bagel', 'Bagel')),
    specialty(
        'crispy-fish-skin',
        names(
            'Crispy fish skin',
            'Knusprige Fischhaut',
            'Хрустящая рыбья кожа',
            'Piel de pescado crujiente',
            'Chrupiąca skóra ryby',
            'Peau de poisson croustillante',
            'Pelle di pesce croccante',
        ),
    ),
    specialty(
        'double-skin-milk',
        names(
            'Double skin milk',
            'Doppelhaut-Milch',
            'Двойная молочная пенка',
            'Leche de doble piel',
            'Mleko o podwójnej skórce',
            'Lait à double peau',
            'Latte a doppia pelle',
        ),
    ),
    specialty(
        'jidi-congee',
        names(
            'Jidi congee',
            'Jidi-Reisbrei',
            'Каша цзиди',
            'Congee jidi',
            'Owsianka jidi',
            'Congee jidi',
            'Congee jidi',
        ),
    ),
    specialty(
        'rice-noodle-roll',
        names(
            'Rice noodle roll',
            'Reisnudelrolle',
            'Рисовый рулет',
            'Rollo de arroz',
            'Rolka z mąki ryżowej',
            'Rouleau de riz',
            'Involtino di riso',
        ),
    ),
    specialty(
        'coconut-ice-cream',
        names(
            'Coconut ice cream',
            'Kokoseis',
            'Кокосовый пломбир',
            'Helado de coco',
            'Lody kokosowe',
            'Glace à la coco',
            'Gelato al cocco',
        ),
    ),
    specialty(
        'mooncake',
        names(
            'Mooncake',
            'Mondkuchen',
            'Лунный пряник',
            'Pastel de luna',
            'Ciasteczko księżycowe',
            'Gâteau de lune',
            'Torta lunare',
        ),
    ),
    specialty(
        'dim-sum',
        names('Dim sum', 'Dim Sum', 'Димсам', 'Dim sum', 'Dim sum', 'Dim sum', 'Dim sum'),
    ),
    specialty(
        'pyshka',
        names('Pyshka', 'Pyschka', 'Пышка', 'Pyshka', 'Pyszka', 'Pyshka', 'Pyshka'),
    ),
    specialty('bao', names('Bao', 'Bao', 'Бао', 'Bao', 'Bao', 'Bao', 'Bao')),
    specialty('pizza', names('Pizza', 'Pizza', 'Пицца', 'Pizza', 'Pizza', 'Pizza', 'Pizza')),
    specialty(
        'pizza-al-taglio',
        names(
            'Pizza by the slice',
            'Pizza al taglio',
            'Пицца с прилавка',
            'Pizza al corte',
            'Pizza na kawałki',
            'Pizza à la part',
            'Pizza al taglio',
        ),
    ),
    specialty('pasta', names('Pasta', 'Pasta', 'Паста', 'Pasta', 'Makaron', 'Pâtes', 'Pasta')),
    specialty(
        'burger',
        names('Burger', 'Burger', 'Бургер', 'Hamburguesa', 'Burger', 'Burger', 'Burger'),
    ),
    specialty(
        'grilled-chicken',
        names(
            'Grilled chicken',
            'Grillhähnchen',
            'Цыплёнок с решётки',
            'Pollo a la brasa',
            'Kurczak z rusztu',
            'Poulet grillé',
            'Pollo alla griglia',
        ),
    ),
    specialty(
        'snail',
        names('Snails', 'Schnecken', 'Улитки', 'Caracoles', 'Ślimaki', 'Escargots', 'Lumache'),
    ),
    specialty('cheese', names('Cheese', 'Käse', 'Сыр', 'Queso', 'Ser', 'Fromage', 'Formaggio')),
    specialty(
        'truffle-sandwich',
        names(
            'Truffle sandwich',
            'Trüffel-Panini',
            'Трюфельное панини',
            'Bocadillo de trufa',
            'Kanapka z truflą',
            'Panini à la truffe',
            'Panino al tartufo',
        ),
    ),
    specialty('tapas', names('Tapas', 'Tapas', 'Тапас', 'Tapas', 'Tapas', 'Tapas', 'Tapas')),
    specialty('kebab', names('Kebab', 'Kebab', 'Кебаб', 'Kebab', 'Kebab', 'Kebab', 'Kebab')),
    specialty('manti', names('Manti', 'Manti', 'Манты', 'Manti', 'Manty', 'Manti', 'Manti')),
    specialty('fish', names('Fish', 'Fisch', 'Рыба', 'Pescado', 'Ryba', 'Poisson', 'Pesce')),
    specialty(
        'ice-cream',
        names('Ice cream', 'Eis', 'Мороженое', 'Helado', 'Lody', 'Glace', 'Gelato'),
    ),
    specialty(
        'pastry',
        names('Pastry', 'Gebäck', 'Выпечка', 'Bollería', 'Wypieki', 'Viennoiserie', 'Pasticceria'),
    ),
    specialty(
        'egg-coffee',
        names(
            'Egg coffee',
            'Eierkaffee',
            'Яичный кофе',
            'Café con huevo',
            'Kawa jajeczna',
            'Café à l’œuf',
            'Caffè all’uovo',
        ),
    ),
    specialty(
        'jenever',
        names(
            'Jenever',
            'Genever',
            'Женевер',
            'Ginebra holandesa',
            'Jenever',
            'Genièvre',
            'Jenever',
        ),
    ),
    specialty(
        'ya-dong',
        names('Ya dong', 'Ya Dong', 'Ядонг', 'Ya dong', 'Ya dong', 'Ya dong', 'Ya dong'),
    ),
    specialty('sherry', names('Sherry', 'Sherry', 'Херес', 'Jerez', 'Sherry', 'Xérès', 'Sherry')),
    specialty(
        'vermouth',
        names('Vermouth', 'Wermut', 'Вермут', 'Vermú', 'Wermut', 'Vermouth', 'Vermut'),
    ),
    specialty('whisky', names('Whisky', 'Whisky', 'Виски', 'Whisky', 'Whisky', 'Whisky', 'Whisky')),
    specialty(
        'riesling',
        names('Riesling', 'Riesling', 'Рислинг', 'Riesling', 'Riesling', 'Riesling', 'Riesling'),
    ),
    specialty(
        'pajarete',
        names('Pajarete', 'Pajarete', 'Пахарете', 'Pajarete', 'Pajarete', 'Pajarete', 'Pajarete'),
    ),
    specialty(
        'borscht',
        names('Borscht', 'Borschtsch', 'Борщ', 'Borsch', 'Barszcz', 'Bortsch', 'Boršč'),
    ),
    specialty('shchi', names('Shchi', 'Schtschi', 'Щи', 'Shchi', 'Szczi', 'Chtchi', 'Šči')),
    specialty(
        'solyanka',
        names('Solyanka', 'Soljanka', 'Солянка', 'Solianka', 'Solanka', 'Solianka', 'Soljanka'),
    ),
    specialty('ukha', names('Ukha', 'Ucha', 'Уха', 'Ujá', 'Ucha', 'Oukha', 'Ucha')),
    specialty(
        'okroshka',
        names('Okroshka', 'Okroschka', 'Окрошка', 'Okroshka', 'Okroszka', 'Okrochka', 'Okroška'),
    ),
    specialty(
        'onion-soup',
        names(
            'French onion soup',
            'Zwiebelsuppe',
            'Луковый суп',
            'Sopa de cebolla',
            'Zupa cebulowa',
            'Soupe à l’oignon',
            'Zuppa di cipolle',
        ),
    ),
    specialty(
        'bouillabaisse',
        names(
            'Bouillabaisse',
            'Bouillabaisse',
            'Буйабес',
            'Bullabesa',
            'Bouillabaisse',
            'Bouillabaisse',
            'Bouillabaisse',
        ),
    ),
    specialty('zurek', names('Żurek', 'Żurek', 'Журек', 'Żurek', 'Żurek', 'Żurek', 'Żurek')),
    specialty(
        'hungarian-fish-soup',
        names(
            'Hungarian fish soup',
            'Ungarische Fischsuppe',
            'Венгерский рыбный суп',
            'Sopa de pescado húngara',
            'Węgierska zupa rybna',
            'Soupe de poisson hongroise',
            'Zuppa di pesce ungherese',
        ),
    ),
    specialty(
        'pea-soup',
        names(
            'Pea soup',
            'Erbsensuppe',
            'Гороховый суп',
            'Sopa de guisantes',
            'Grochówka',
            'Soupe de pois',
            'Zuppa di piselli',
        ),
    ),
    specialty(
        'pork-knuckle',
        names(
            'Pork knuckle',
            'Schweinshaxe',
            'Рулька',
            'Codillo',
            'Golonka',
            'Jarret de porc',
            'Stinco di maiale',
        ),
    ),
    specialty(
        'weisswurst',
        names(
            'Munich sausages',
            'Weißwurst',
            'Мюнхенские сосиски',
            'Salchichas de Múnich',
            'Biała kiełbasa',
            'Saucisses de Munich',
            'Würstel bianchi',
        ),
    ),
    specialty(
        'bratwurst',
        names(
            'Bratwurst',
            'Bratwurst',
            'Братвурст',
            'Bratwurst',
            'Bratwurst',
            'Bratwurst',
            'Bratwurst',
        ),
    ),
    specialty(
        'currywurst',
        names(
            'Currywurst',
            'Currywurst',
            'Карривурст',
            'Currywurst',
            'Currywurst',
            'Currywurst',
            'Currywurst',
        ),
    ),
    specialty(
        'leberkaese',
        names(
            'Leberkäse',
            'Leberkäse',
            'Леберкезе',
            'Leberkäse',
            'Leberkäse',
            'Leberkäse',
            'Leberkäse',
        ),
    ),
    specialty(
        'schnitzel',
        names('Schnitzel', 'Schnitzel', 'Шницель', 'Escalope', 'Sznycel', 'Escalope', 'Cotoletta'),
    ),
    specialty(
        'goulash',
        names('Goulash', 'Gulasch', 'Гуляш', 'Gulash', 'Gulasz', 'Goulash', 'Gulasch'),
    ),
    specialty(
        'paprikash',
        names(
            'Paprikash',
            'Paprikasch',
            'Паприкаш',
            'Paprikash',
            'Paprykarz',
            'Paprikash',
            'Paprikash',
        ),
    ),
    specialty(
        'czech-roast',
        names(
            'Czech roast with dumplings',
            'Böhmischer Braten',
            'Жаркое с кнедликами',
            'Asado checo con albóndigas de pan',
            'Pieczeń czeska z knedlikami',
            'Rôti tchèque et knedliky',
            'Arrosto ceco con canederli',
        ),
    ),
    specialty(
        'svickova',
        names('Svíčková', 'Svíčková', 'Свичкова', 'Svíčková', 'Svíčková', 'Svíčková', 'Svíčková'),
    ),
    specialty(
        'tafelspitz',
        names(
            'Tafelspitz',
            'Tafelspitz',
            'Тафельшпиц',
            'Tafelspitz',
            'Tafelspitz',
            'Tafelspitz',
            'Tafelspitz',
        ),
    ),
    specialty(
        'sauerbraten',
        names(
            'Sauerbraten',
            'Sauerbraten',
            'Заурбратен',
            'Sauerbraten',
            'Sauerbraten',
            'Sauerbraten',
            'Sauerbraten',
        ),
    ),
    specialty(
        'flemish-stew',
        names(
            'Flemish beef stew',
            'Carbonade',
            'Фламандское рагу',
            'Estofado flamenco',
            'Gulasz flandryjski',
            'Carbonade flamande',
            'Stufato delle Fiandre',
        ),
    ),
    specialty(
        'fiorentina',
        names(
            'Florentine steak',
            'Bistecca alla fiorentina',
            'Флорентийский стейк',
            'Bistec a la florentina',
            'Stek florencki',
            'Steak florentin',
            'Bistecca alla fiorentina',
        ),
    ),
    specialty(
        'steak-frites',
        names(
            'Steak frites',
            'Steak frites',
            'Стейк с фри',
            'Bistec con patatas',
            'Stek z frytkami',
            'Steak frites',
            'Bistecca e patatine',
        ),
    ),
    specialty(
        'porchetta',
        names(
            'Porchetta',
            'Porchetta',
            'Поркетта',
            'Porchetta',
            'Porchetta',
            'Porchetta',
            'Porchetta',
        ),
    ),
    specialty(
        'ossobuco',
        names('Ossobuco', 'Ossobuco', 'Оссобуко', 'Ossobuco', 'Ossobuco', 'Osso buco', 'Ossobuco'),
    ),
    specialty(
        'lampredotto',
        names(
            'Lampredotto',
            'Lampredotto',
            'Лампредотто',
            'Lampredotto',
            'Lampredotto',
            'Lampredotto',
            'Lampredotto',
        ),
    ),
    specialty(
        'sunday-roast',
        names(
            'Sunday roast',
            'Sonntagsbraten',
            'Воскресное жаркое',
            'Asado del domingo',
            'Niedzielna pieczeń',
            'Rôti du dimanche',
            'Arrosto della domenica',
        ),
    ),
    specialty(
        'meat-pie',
        names(
            'Meat pie',
            'Fleischpastete',
            'Мясной пирог',
            'Empanada de carne',
            'Placek mięsny',
            'Tourte à la viande',
            'Pasticcio di carne',
        ),
    ),
    specialty(
        'haggis',
        names('Haggis', 'Haggis', 'Хаггис', 'Haggis', 'Haggis', 'Haggis', 'Haggis'),
    ),
    specialty('peka', names('Peka', 'Peka', 'Мясо под пекой', 'Peka', 'Peka', 'Peka', 'Peka')),
    specialty(
        'chicken-kiev',
        names(
            'Chicken Kyiv',
            'Kiewer Kotelett',
            'Котлета по-киевски',
            'Pollo Kiev',
            'Kotlet de volaille',
            'Poulet à la Kiev',
            'Pollo alla Kiev',
        ),
    ),
    specialty(
        'shashlik',
        names('Shashlik', 'Schaschlik', 'Шашлык', 'Shashlik', 'Szaszłyk', 'Chachlik', 'Shashlik'),
    ),
    specialty(
        'iskender',
        names('Iskender', 'Iskender', 'Искендер', 'Iskender', 'Iskender', 'Iskender', 'Iskender'),
    ),
    specialty(
        'duck-confit',
        names(
            'Duck confit',
            'Entenconfit',
            'Утка конфи',
            'Confit de pato',
            'Confit z kaczki',
            'Confit de canard',
            'Confit d’anatra',
        ),
    ),
    specialty(
        'shawarma',
        names('Shawarma', 'Döner', 'Шаверма', 'Shawarma', 'Szawarma', 'Chawarma', 'Shawarma'),
    ),
    specialty('gyros', names('Gyros', 'Gyros', 'Гирос', 'Gyros', 'Gyros', 'Gyros', 'Gyros')),
    specialty(
        'souvlaki',
        names('Souvlaki', 'Souvlaki', 'Сувлаки', 'Souvlaki', 'Souvlaki', 'Souvlaki', 'Souvlaki'),
    ),
    specialty(
        'cevapi',
        names('Ćevapi', 'Ćevapčići', 'Чевапи', 'Ćevapi', 'Ćevapi', 'Ćevapi', 'Ćevapi'),
    ),
    specialty(
        'pljeskavica',
        names(
            'Pljeskavica',
            'Pljeskavica',
            'Плескавица',
            'Pljeskavica',
            'Pljeskavica',
            'Pljeskavica',
            'Pljeskavica',
        ),
    ),
    specialty(
        'lahmacun',
        names('Lahmacun', 'Lahmacun', 'Лахмаджун', 'Lahmacun', 'Lahmacun', 'Lahmacun', 'Lahmacun'),
    ),
    specialty('pide', names('Pide', 'Pide', 'Пиде', 'Pide', 'Pide', 'Pide', 'Pide')),
    specialty(
        'falafel',
        names('Falafel', 'Falafel', 'Фалафель', 'Falafel', 'Falafel', 'Falafel', 'Falafel'),
    ),
    specialty(
        'hummus',
        names('Hummus', 'Hummus', 'Хумус', 'Hummus', 'Hummus', 'Houmous', 'Hummus'),
    ),
    specialty(
        'shakshuka',
        names(
            'Shakshuka',
            'Shakshuka',
            'Шакшука',
            'Shakshuka',
            'Szakszuka',
            'Chakchouka',
            'Shakshuka',
        ),
    ),
    specialty(
        'pelmeni',
        names('Pelmeni', 'Pelmeni', 'Пельмени', 'Pelmeni', 'Pielmieni', 'Pelmeni', 'Pelmeni'),
    ),
    specialty(
        'vareniki',
        names('Vareniki', 'Wareniki', 'Вареники', 'Vareniki', 'Wareniki', 'Vareniki', 'Vareniki'),
    ),
    specialty(
        'pierogi',
        names('Pierogi', 'Pierogi', 'Пьероги', 'Pierogi', 'Pierogi', 'Pierogi', 'Pierogi'),
    ),
    specialty(
        'khinkali',
        names('Khinkali', 'Chinkali', 'Хинкали', 'Jinkali', 'Chinkali', 'Khinkali', 'Khinkali'),
    ),
    specialty(
        'cheburek',
        names(
            'Cheburek',
            'Tscheburek',
            'Чебурек',
            'Cheburek',
            'Czeburek',
            'Tchebourek',
            'Cheburek',
        ),
    ),
    specialty(
        'belyash',
        names('Belyash', 'Beljasch', 'Беляш', 'Belyash', 'Bieliasz', 'Beliash', 'Belyash'),
    ),
    specialty('burek', names('Burek', 'Börek', 'Бурек', 'Burek', 'Burek', 'Börek', 'Börek')),
    specialty(
        'khachapuri',
        names(
            'Khachapuri',
            'Chatschapuri',
            'Хачапури',
            'Jachapuri',
            'Chaczapuri',
            'Khatchapouri',
            'Khachapuri',
        ),
    ),
    specialty(
        'pirozhki',
        names('Pirozhki', 'Piroggen', 'Пирожки', 'Pirozhki', 'Pirożki', 'Pirojkis', 'Pirozhki'),
    ),
    specialty('blini', names('Blini', 'Blini', 'Блины', 'Blini', 'Blini', 'Blini', 'Blini')),
    specialty(
        'syrniki',
        names('Syrniki', 'Syrniki', 'Сырники', 'Syrniki', 'Syrniki', 'Syrniki', 'Syrniki'),
    ),
    specialty(
        'draniki',
        names('Draniki', 'Draniki', 'Драники', 'Draniki', 'Draniki', 'Draniki', 'Draniki'),
    ),
    specialty(
        'galette',
        names(
            'Buckwheat galette',
            'Buchweizen-Galette',
            'Гречневая галета',
            'Galette de trigo sarraceno',
            'Galette gryczana',
            'Galette de sarrasin',
            'Galette di grano saraceno',
        ),
    ),
    specialty(
        'carbonara',
        names(
            'Carbonara',
            'Carbonara',
            'Карбонара',
            'Carbonara',
            'Carbonara',
            'Carbonara',
            'Carbonara',
        ),
    ),
    specialty(
        'cacio-e-pepe',
        names(
            'Cacio e pepe',
            'Cacio e pepe',
            'Качо э пепе',
            'Cacio e pepe',
            'Cacio e pepe',
            'Cacio e pepe',
            'Cacio e pepe',
        ),
    ),
    specialty(
        'amatriciana',
        names(
            'Amatriciana',
            'Amatriciana',
            'Аматричана',
            'Amatriciana',
            'Amatriciana',
            'Amatriciana',
            'Amatriciana',
        ),
    ),
    specialty('ragu', names('Ragù', 'Ragù', 'Рагу', 'Ragú', 'Ragù', 'Ragù', 'Ragù')),
    specialty(
        'lasagna',
        names('Lasagna', 'Lasagne', 'Лазанья', 'Lasaña', 'Lasagne', 'Lasagnes', 'Lasagne'),
    ),
    specialty(
        'risotto',
        names('Risotto', 'Risotto', 'Ризотто', 'Risotto', 'Risotto', 'Risotto', 'Risotto'),
    ),
    specialty(
        'black-risotto',
        names(
            'Black risotto',
            'Schwarzes Risotto',
            'Чёрный ризотто',
            'Risotto negro',
            'Czarne risotto',
            'Risotto noir',
            'Risotto nero',
        ),
    ),
    specialty(
        'focaccia',
        names('Focaccia', 'Focaccia', 'Фокачча', 'Focaccia', 'Focaccia', 'Focaccia', 'Focaccia'),
    ),
    specialty('socca', names('Socca', 'Socca', 'Сокка', 'Socca', 'Socca', 'Socca', 'Socca')),
    specialty(
        'arancini',
        names('Arancini', 'Arancini', 'Аранчини', 'Arancini', 'Arancini', 'Arancini', 'Arancini'),
    ),
    specialty(
        'obwarzanek',
        names(
            'Obwarzanek',
            'Obwarzanek',
            'Обваржанек',
            'Obwarzanek',
            'Obwarzanek',
            'Obwarzanek',
            'Obwarzanek',
        ),
    ),
    specialty(
        'pretzel',
        names('Pretzel', 'Brezel', 'Брецель', 'Bretzel', 'Precel', 'Bretzel', 'Brezel'),
    ),
    specialty('simit', names('Simit', 'Simit', 'Симит', 'Simit', 'Simit', 'Simit', 'Simit')),
    specialty(
        'baguette',
        names('Baguette', 'Baguette', 'Багет', 'Bagette', 'Bagietka', 'Baguette', 'Baguette'),
    ),
    specialty(
        'langos',
        names('Lángos', 'Lángos', 'Лангош', 'Lángos', 'Lángos', 'Lángos', 'Lángos'),
    ),
    specialty(
        'halusky',
        names(
            'Halušky with bryndza',
            'Bryndzové halušky',
            'Галушки с брынзой',
            'Halušky con bryndza',
            'Halušky z bryndzą',
            'Halušky à la bryndza',
            'Halušky con bryndza',
        ),
    ),
    specialty(
        'spaetzle',
        names('Spätzle', 'Spätzle', 'Шпецле', 'Spätzle', 'Spätzle', 'Spätzle', 'Spätzle'),
    ),
    specialty(
        'maultaschen',
        names(
            'Maultaschen',
            'Maultaschen',
            'Маульташен',
            'Maultaschen',
            'Maultaschen',
            'Maultaschen',
            'Maultaschen',
        ),
    ),
    specialty(
        'flammkuchen',
        names(
            'Flammkuchen',
            'Flammkuchen',
            'Фламмкухен',
            'Flammkuchen',
            'Flammkuchen',
            'Tarte flambée',
            'Flammkuchen',
        ),
    ),
    specialty(
        'oysters',
        names('Oysters', 'Austern', 'Устрицы', 'Ostras', 'Ostrygi', 'Huîtres', 'Ostriche'),
    ),
    specialty(
        'herring',
        names('Herring', 'Hering', 'Селёдка', 'Arenque', 'Śledź', 'Hareng', 'Aringa'),
    ),
    specialty(
        'gravlax',
        names('Gravlax', 'Gravlax', 'Гравлакс', 'Gravlax', 'Gravlax', 'Gravlax', 'Gravlax'),
    ),
    specialty(
        'smoked-fish',
        names(
            'Smoked fish',
            'Räucherfisch',
            'Копчёная рыба',
            'Pescado ahumado',
            'Wędzona ryba',
            'Poisson fumé',
            'Pesce affumicato',
        ),
    ),
    specialty(
        'bacalhau',
        names('Bacalhau', 'Bacalhau', 'Бакальяу', 'Bacalao', 'Bacalhau', 'Morue', 'Baccalà'),
    ),
    specialty(
        'grilled-sardines',
        names(
            'Grilled sardines',
            'Gegrillte Sardinen',
            'Сардины на гриле',
            'Sardinas a la brasa',
            'Sardynki z grilla',
            'Sardines grillées',
            'Sardine alla griglia',
        ),
    ),
    specialty(
        'octopus',
        names('Octopus', 'Oktopus', 'Осьминог', 'Pulpo', 'Ośmiornica', 'Poulpe', 'Polpo'),
    ),
    specialty(
        'mussels',
        names('Mussels', 'Miesmuscheln', 'Мидии', 'Mejillones', 'Małże', 'Moules', 'Cozze'),
    ),
    specialty(
        'fish-and-chips',
        names(
            'Fish and chips',
            'Fish and Chips',
            'Рыба с картошкой',
            'Fish and chips',
            'Fish and chips',
            'Fish and chips',
            'Fish and chips',
        ),
    ),
    specialty(
        'fried-fish',
        names(
            'Fried fish',
            'Frittierter Fisch',
            'Жареная рыба',
            'Pescado frito',
            'Smażona ryba',
            'Poisson frit',
            'Pesce fritto',
        ),
    ),
    specialty(
        'fish-bread',
        names(
            'Fish in bread',
            'Fischbrötchen',
            'Рыба в хлебе',
            'Pescado en pan',
            'Ryba w bułce',
            'Poisson en pain',
            'Pesce nel pane',
        ),
    ),
    specialty(
        'seafood-platter',
        names(
            'Seafood platter',
            'Meeresfrüchteplatte',
            'Плато морепродуктов',
            'Mariscada',
            'Taca owoców morza',
            'Plateau de fruits de mer',
            'Plateau di frutti di mare',
        ),
    ),
    specialty(
        'calamari-bocadillo',
        names(
            'Fried calamari sandwich',
            'Calamares-Brötchen',
            'Бутерброд с кальмаром',
            'Bocadillo de calamares',
            'Kanapka z kalmarami',
            'Sandwich aux calmars',
            'Panino ai calamari',
        ),
    ),
    specialty(
        'strudel',
        names('Strudel', 'Strudel', 'Штрудель', 'Strudel', 'Strudel', 'Strudel', 'Strudel'),
    ),
    specialty(
        'sacher',
        names(
            'Sachertorte',
            'Sachertorte',
            'Торт Захер',
            'Tarta Sacher',
            'Sachertorte',
            'Sachertorte',
            'Sachertorte',
        ),
    ),
    specialty(
        'kaiserschmarrn',
        names(
            'Kaiserschmarrn',
            'Kaiserschmarrn',
            'Кайзершмаррн',
            'Kaiserschmarrn',
            'Kaiserschmarrn',
            'Kaiserschmarrn',
            'Kaiserschmarrn',
        ),
    ),
    specialty(
        'salzburger-nockerl',
        names(
            'Salzburger Nockerl',
            'Salzburger Nockerln',
            'Зальцбургский нокерль',
            'Salzburger Nockerl',
            'Salzburger Nockerl',
            'Salzburger Nockerl',
            'Salzburger Nockerl',
        ),
    ),
    specialty('waffle', names('Waffle', 'Waffel', 'Вафли', 'Gofre', 'Gofry', 'Gaufre', 'Waffle')),
    specialty(
        'poffertjes',
        names(
            'Poffertjes',
            'Poffertjes',
            'Поффертьес',
            'Poffertjes',
            'Poffertjes',
            'Poffertjes',
            'Poffertjes',
        ),
    ),
    specialty(
        'cinnamon-bun',
        names(
            'Cinnamon bun',
            'Zimtschnecke',
            'Булочка с корицей',
            'Rollo de canela',
            'Bułka cynamonowa',
            'Brioche à la cannelle',
            'Brioche alla cannella',
        ),
    ),
    specialty(
        'trdelnik',
        names('Trdelník', 'Trdelník', 'Трдельник', 'Trdelník', 'Trdelník', 'Trdelník', 'Trdelník'),
    ),
    specialty(
        'kurtoskalacs',
        names(
            'Kürtőskalács',
            'Kürtőskalács',
            'Кюртошкалач',
            'Kürtőskalács',
            'Kürtőskalács',
            'Kürtőskalács',
            'Kürtőskalács',
        ),
    ),
    specialty(
        'churros',
        names('Churros', 'Churros', 'Чуррос', 'Churros', 'Churros', 'Churros', 'Churros'),
    ),
    specialty(
        'pastel-de-nata',
        names(
            'Pastel de nata',
            'Pastel de nata',
            'Паштел де ната',
            'Pastel de nata',
            'Pastel de nata',
            'Pastel de nata',
            'Pastel de nata',
        ),
    ),
    specialty(
        'tiramisu',
        names('Tiramisu', 'Tiramisù', 'Тирамису', 'Tiramisú', 'Tiramisu', 'Tiramisu', 'Tiramisù'),
    ),
    specialty(
        'cannoli',
        names('Cannoli', 'Cannoli', 'Канноли', 'Cannoli', 'Cannoli', 'Cannoli', 'Cannoli'),
    ),
    specialty(
        'sfogliatella',
        names(
            'Sfogliatella',
            'Sfogliatella',
            'Сфольятелла',
            'Sfogliatella',
            'Sfogliatella',
            'Sfogliatella',
            'Sfogliatella',
        ),
    ),
    specialty(
        'macaron',
        names('Macaron', 'Macaron', 'Макарон', 'Macaron', 'Makaronik', 'Macaron', 'Macaron'),
    ),
    specialty('eclair', names('Éclair', 'Éclair', 'Эклер', 'Éclair', 'Ekler', 'Éclair', 'Éclair')),
    specialty(
        'baklava',
        names('Baklava', 'Baklava', 'Баклава', 'Baklava', 'Baklawa', 'Baklava', 'Baklava'),
    ),
    specialty(
        'kunefe',
        names('Künefe', 'Künefe', 'Кюнефе', 'Künefe', 'Künefe', 'Künefe', 'Künefe'),
    ),
    specialty(
        'loukoumades',
        names(
            'Loukoumades',
            'Loukoumades',
            'Лукумадес',
            'Loukoumades',
            'Loukoumades',
            'Loukoumades',
            'Loukoumades',
        ),
    ),
    specialty(
        'bougatsa',
        names('Bougatsa', 'Bougatsa', 'Бугаца', 'Bougatsa', 'Bougatsa', 'Bougatsa', 'Bougatsa'),
    ),
    specialty(
        'chocolate',
        names(
            'Chocolate',
            'Schokolade',
            'Шоколад',
            'Chocolate',
            'Czekolada',
            'Chocolat',
            'Cioccolato',
        ),
    ),
    specialty(
        'fondue',
        names('Fondue', 'Fondue', 'Фондю', 'Fondue', 'Fondue', 'Fondue', 'Fonduta'),
    ),
    specialty(
        'raclette',
        names('Raclette', 'Raclette', 'Раклет', 'Raclette', 'Raclette', 'Raclette', 'Raclette'),
    ),
    specialty('rosti', names('Rösti', 'Rösti', 'Рёшти', 'Rösti', 'Rösti', 'Rösti', 'Rösti')),
    specialty(
        'choucroute',
        names(
            'Choucroute',
            'Sauerkrautplatte',
            'Шукрут',
            'Chucrut',
            'Kiszonka z kapusty',
            'Choucroute',
            'Crauti',
        ),
    ),
    specialty(
        'tartiflette',
        names(
            'Tartiflette',
            'Tartiflette',
            'Тартифлет',
            'Tartiflette',
            'Tartiflette',
            'Tartiflette',
            'Tartiflette',
        ),
    ),
    specialty('quiche', names('Quiche', 'Quiche', 'Киш', 'Quiche', 'Quiche', 'Quiche', 'Quiche')),
    specialty(
        'cassoulet',
        names(
            'Cassoulet',
            'Cassoulet',
            'Кассуле',
            'Cassoulet',
            'Cassoulet',
            'Cassoulet',
            'Cassoulet',
        ),
    ),
    specialty('bigos', names('Bigos', 'Bigos', 'Бигос', 'Bigos', 'Bigos', 'Bigos', 'Bigos')),
    specialty(
        'zapiekanka',
        names(
            'Zapiekanka',
            'Zapiekanka',
            'Запеканка',
            'Zapiekanka',
            'Zapiekanka',
            'Zapiekanka',
            'Zapiekanka',
        ),
    ),
    specialty(
        'fried-cheese',
        names(
            'Fried cheese',
            'Gebackener Käse',
            'Жареный сыр',
            'Queso frito',
            'Smażony ser',
            'Fromage frit',
            'Formaggio fritto',
        ),
    ),
    specialty(
        'stamppot',
        names('Stamppot', 'Stamppot', 'Стамппот', 'Stamppot', 'Stamppot', 'Stamppot', 'Stamppot'),
    ),
    specialty(
        'frites',
        names('Frites', 'Fritten', 'Фриты', 'Fritas', 'Frytki', 'Frites', 'Patatine fritte'),
    ),
    specialty(
        'turkish-breakfast',
        names(
            'Turkish breakfast',
            'Türkisches Frühstück',
            'Турецкий завтрак',
            'Desayuno turco',
            'Tureckie śniadanie',
            'Petit-déjeuner turc',
            'Colazione turca',
        ),
    ),
    specialty(
        'english-breakfast',
        names(
            'English breakfast',
            'Englisches Frühstück',
            'Английский завтрак',
            'Desayuno inglés',
            'Angielskie śniadanie',
            'Petit-déjeuner anglais',
            'Colazione inglese',
        ),
    ),
    specialty(
        'smorrebrod',
        names(
            'Smørrebrød',
            'Smørrebrød',
            'Сморреброд',
            'Smørrebrød',
            'Smørrebrød',
            'Smørrebrød',
            'Smørrebrød',
        ),
    ),
    specialty(
        'swedish-meatballs',
        names(
            'Swedish meatballs',
            'Schwedische Fleischbällchen',
            'Шведские фрикадельки',
            'Albóndigas suecas',
            'Szwedzkie klopsiki',
            'Boulettes suédoises',
            'Polpette svedesi',
        ),
    ),
    specialty(
        'icelandic-hot-dog',
        names(
            'Icelandic hot dog',
            'Isländischer Hotdog',
            'Исландский хот-дог',
            'Perrito islandés',
            'Islandzki hot dog',
            'Hot-dog islandais',
            'Hot dog islandese',
        ),
    ),
    specialty(
        'moussaka',
        names('Moussaka', 'Moussaka', 'Мусака', 'Musaka', 'Musaka', 'Moussaka', 'Moussaka'),
    ),
    specialty('plov', names('Plov', 'Plov', 'Плов', 'Plov', 'Pilaw', 'Plov', 'Plov')),
    specialty(
        'lagman',
        names('Lagman', 'Lagman', 'Лагман', 'Lagman', 'Lagman', 'Lagman', 'Lagman'),
    ),
    specialty('samsa', names('Samsa', 'Samsa', 'Самса', 'Samsa', 'Samsa', 'Samsa', 'Samsa')),
    specialty(
        'pastrami',
        names('Pastrami', 'Pastrami', 'Пастрами', 'Pastrami', 'Pastrami', 'Pastrami', 'Pastrami'),
    ),
    specialty(
        'truffle',
        names('Truffle', 'Trüffel', 'Трюфель', 'Trufa', 'Trufla', 'Truffe', 'Tartufo'),
    ),
    specialty(
        'mozzarella',
        names(
            'Mozzarella',
            'Mozzarella',
            'Моцарелла',
            'Mozzarella',
            'Mozzarella',
            'Mozzarella',
            'Mozzarella',
        ),
    ),
    specialty(
        'prosciutto',
        names(
            'Prosciutto',
            'Prosciutto',
            'Прошутто',
            'Prosciutto',
            'Prosciutto',
            'Prosciutto',
            'Prosciutto',
        ),
    ),
    specialty(
        'jamon',
        names('Jamón', 'Jamón', 'Хамон', 'Jamón', 'Szynka serrano', 'Jambon', 'Jamón'),
    ),
    specialty(
        'pintxos',
        names('Pintxos', 'Pintxos', 'Пинчос', 'Pintxos', 'Pintxos', 'Pintxos', 'Pintxos'),
    ),
    specialty(
        'paella',
        names('Paella', 'Paella', 'Паэлья', 'Paella', 'Paella', 'Paella', 'Paella'),
    ),
    specialty(
        'fideua',
        names('Fideuà', 'Fideuà', 'Фидеуа', 'Fideuà', 'Fideuà', 'Fideuà', 'Fideuà'),
    ),
    specialty(
        'spanish-tortilla',
        names(
            'Spanish tortilla',
            'Spanische Tortilla',
            'Тортилья',
            'Tortilla de patatas',
            'Hiszpańska tortilla',
            'Tortilla espagnole',
            'Tortilla di patate',
        ),
    ),
    specialty(
        'gazpacho',
        names('Gazpacho', 'Gazpacho', 'Гаспачо', 'Gazpacho', 'Gazpacho', 'Gaspacho', 'Gazpacho'),
    ),
    specialty(
        'cocido',
        names('Cocido', 'Cocido', 'Косидо', 'Cocido', 'Cocido', 'Cocido', 'Cocido'),
    ),
    specialty(
        'calcots',
        names('Calçots', 'Calçots', 'Кальсоты', 'Calçots', 'Calçots', 'Calçots', 'Calçots'),
    ),
    specialty(
        'francesinha',
        names(
            'Francesinha',
            'Francesinha',
            'Франсезинья',
            'Francesinha',
            'Francesinha',
            'Francesinha',
            'Francesinha',
        ),
    ),
    specialty(
        'bifana',
        names('Bifana', 'Bifana', 'Бифана', 'Bifana', 'Bifana', 'Bifana', 'Bifana'),
    ),
    specialty('cider', names('Cider', 'Apfelwein', 'Сидр', 'Sidra', 'Cydr', 'Cidre', 'Sidro')),
    specialty('rakia', names('Rakia', 'Rakija', 'Ракия', 'Rakia', 'Rakija', 'Rakia', 'Rakia')),
    specialty('ouzo', names('Ouzo', 'Ouzo', 'Узо', 'Ouzo', 'Ouzo', 'Ouzo', 'Ouzo')),
    specialty(
        'schnapps',
        names('Schnapps', 'Schnaps', 'Шнапс', 'Aguardiente', 'Schnaps', 'Schnaps', 'Schnapps'),
    ),
    specialty(
        'milk-tea',
        names(
            'Milk tea',
            'Milchtee',
            'Молочный чай',
            'Té con leche',
            'Herbata z mlekiem',
            'Thé au lait',
            'Tè al latte',
        ),
    ),
    specialty(
        'pan-mee',
        names('Pan mee', 'Pan Mee', 'Пан-ми', 'Pan mee', 'Pan mee', 'Pan mee', 'Pan mee'),
    ),
    specialty(
        'pork-chop-rice',
        names(
            'Pork chop rice',
            'Kotelett mit Reis',
            'Отбивная на рисе',
            'Chuleta con arroz',
            'Kotlet z ryżem',
            'Côtelette au riz',
            'Braciola con riso',
        ),
    ),
    specialty(
        'suckling-pig',
        names(
            'Suckling pig',
            'Spanferkel',
            'Молочный поросёнок',
            'Cochinillo',
            'Prosię',
            'Cochon de lait',
            'Maialino',
        ),
    ),
    specialty('tacos', names('Tacos', 'Tacos', 'Тако', 'Tacos', 'Tacos', 'Tacos', 'Tacos')),
    specialty(
        'ribs',
        names('Ribs', 'Rippchen', 'Рёбрышки', 'Costillas', 'Żeberka', 'Travers de porc', 'Costine'),
    ),
    specialty(
        'banh-mi-chao',
        names(
            'Bánh mì chảo',
            'Bánh mì chảo',
            'Бань ми чао',
            'Bánh mì chảo',
            'Bánh mì chảo',
            'Bánh mì chảo',
            'Bánh mì chảo',
        ),
    ),
    specialty(
        'menemen',
        names('Menemen', 'Menemen', 'Менемен', 'Menemen', 'Menemen', 'Menemen', 'Menemen'),
    ),
    specialty('sogus', names('Söğüş', 'Söğüş', 'Сёгюш', 'Söğüş', 'Söğüş', 'Söğüş', 'Söğüş')),
    specialty(
        'bibimbap',
        names('Bibimbap', 'Bibimbap', 'Пибимпап', 'Bibimbap', 'Bibimbap', 'Bibimbap', 'Bibimbap'),
    ),
    specialty(
        'cocktails',
        names('Cocktails', 'Cocktails', 'Коктейли', 'Cócteles', 'Koktajle', 'Cocktails', 'Cocktail'),
    ),
    specialty('gin', names('Gin', 'Gin', 'Джин', 'Ginebra', 'Gin', 'Gin', 'Gin')),
    specialty('beer', names('Beer', 'Bier', 'Пиво', 'Cerveza', 'Piwo', 'Bière', 'Birra')),
    specialty('coffee', names('Coffee', 'Kaffee', 'Кофе', 'Café', 'Kawa', 'Café', 'Caffè')),
    specialty('wine', names('Wine', 'Wein', 'Вино', 'Vino', 'Wino', 'Vin', 'Vino')),
] as const;

export type IntakeSpecialty = (typeof ESTABLISHMENT_SPECIALTIES)[number]['code'];

export const INTAKE_SPECIALTIES: readonly IntakeSpecialty[] = ESTABLISHMENT_SPECIALTIES.map(
    (row) => row.code,
);

const BY_CODE = new Map<string, SpecialtyNames>(
    ESTABLISHMENT_SPECIALTIES.map((row) => [row.code, row.names]),
);

const LANGUAGE_VALUES = new Set<string>(Object.values(Languages));

export function specialtiesOf(values: readonly string[] | null | undefined): IntakeSpecialty[] {
    const out: IntakeSpecialty[] = [];

    for (const value of values || []) {
        if (!BY_CODE.has(value) || out.includes(value as IntakeSpecialty)) continue;
        out.push(value as IntakeSpecialty);
        if (out.length >= 2) break;
    }

    return out;
}

export function specialtyLabel(code: string, language: string): string | null {
    const namesForCode = BY_CODE.get(code);

    if (!namesForCode) return null;

    const key = LANGUAGE_VALUES.has(language) ? (language as Languages) : Languages.EN;

    return namesForCode[key] || namesForCode[Languages.EN];
}

export function specialtyNames(
    codes: readonly string[] | null | undefined,
    language: string,
): string[] {
    const out: string[] = [];

    for (const code of specialtiesOf(codes)) {
        const label = specialtyLabel(code, language);

        if (!label || out.includes(label)) continue;
        out.push(label);
    }

    return out;
}

export function specialtyOptions(
    language: string,
): Array<{ code: IntakeSpecialty; label: string }> {
    return ESTABLISHMENT_SPECIALTIES.map((row) => ({
        code: row.code,
        label: specialtyLabel(row.code, language) || row.names[Languages.EN],
    }));
}
